// We are disabling deprecation rule in order to ensure the component still works for retrocompatibility
/* eslint-disable @typescript-eslint/no-deprecated */
import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { XYChartLegend } from './components/legend/XYChartLegend.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_LOADING_STATE,
} from './constants/defaults.js';
import { XYChartAxesConfigContext } from './context/axes/XYChartAxesConfig.context.js';
import { XYChartAnnotationsConfig } from './context/XYChartAnnotationsConfig.context.js';
import { XYChartColorPaletteContext } from './context/XYChartColorPalette.context.js';
import { XYChartLoadingContext } from './context/XYChartLoading.context.js';
import { XYChartThresholdsContext } from './context/XYChartThresholds.context.js';
import { XYChartToolbarContext } from './context/XYChartToolbar.context.js';
import { XYChartTruncationModeContext } from './context/XYChartTruncationMode.js';
import { useXYChartConfig } from './hooks/useXYChartConfig.js';
import { XYChartAnnotationsDataProvider } from './providers/XYChartAnnotationsDataProvider.js';
import { XYChartAnnotationsShownIndicatorsProvider } from './providers/XYChartAnnotationsShownIndicatorsProvider.js';
import { XYChartColorScalesProvider } from './providers/XYChartColorScalesProvider.js';
import { XYChartChartDataProvider } from './providers/XYChartDataProvider.js';
import { XYChartIdProvider } from './providers/XYChartIdProvider.js';
import { XYChartStoreProvider } from './providers/XYChartStoreProvider.js';
import { XYChartVisibleAxesProvider } from './providers/XYChartVisibleAxesProvider.js';
import { AnnotationsTooltip } from './slots/Annotations/Tooltip.js';
import { DotSeries } from './slots/DotSeries.js';
import { Legend } from './slots/Legend.js';
import { RectSeries } from './slots/RectSeries.js';
import { Threshold } from './slots/Threshold.js';
import { Toolbar } from './slots/Toolbar.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import type { XYChartProps } from './types/xy-chart.js';
import { XYChartRef } from './types/xy-chart.js';
import { isEmptyData } from './utils/is-empty-data.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { buildInternalConfig } from './utils/xy-chart-configuration.js';
import { XYChart as XYChartInternal } from './XYChart.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import { Annotations } from './slots/Annotations/Annotations.js';
import { Marker } from './slots/Annotations/Marker.js';
import { Track } from './slots/Annotations/Track.js';

/**
 * @internal
 */
export const XYChartRoot = /* @__PURE__ */ forwardRef<
  XYChartRef,
  PropsWithChildren<XYChartProps>
>((props, ref) => {
  const {
    data,
    children,
    height,
    width,
    loading = DEFAULT_LOADING_STATE,
    style,
    className,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const slots = iterateSlots(props.children);
  const config = useXYChartConfig(props, slots);
  const { emptyState, errorState } = getChartStatesTemplates(props.children);
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<XYChartRef | HTMLDivElement>([
    ref,
    containerRef,
  ]);

  const {
    hidden: legendHidden,
    ratio: legendRatio,
    onRatioChange,
    position: legendPosition,
  } = config.legend;
  const chartHeight = coerceSizeValue(height ?? DEFAULT_CHART_HEIGHT);

  const internalConfig = buildInternalConfig(config, slots);
  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <XYChartIdProvider>
      <XYChartLoadingContext.Provider value={loading}>
        <XYChartThresholdsContext.Provider value={internalConfig.thresholds}>
          <XYChartAxesConfigContext.Provider value={internalConfig.axes}>
            <XYChartColorPaletteContext.Provider value={config.colorPalette}>
              <XYChartToolbarContext.Provider value={config.toolbar}>
                <XYChartTruncationModeContext.Provider
                  value={{ truncationMode: config.truncationMode }}
                >
                  <XYChartAnnotationsConfig.Provider value={config.annotations}>
                    <XYChartChartDataProvider
                      data={data}
                      seriesVariantsProps={[
                        ...internalConfig.rectSeries,
                        ...internalConfig.dotSeries,
                      ]}
                    >
                      <XYChartAnnotationsDataProvider>
                        <XYChartVisibleAxesProvider>
                          <XYChartColorScalesProvider>
                            <XYChartStoreProvider>
                              <XYChartAnnotationsShownIndicatorsProvider>
                                <ChartLayout
                                  ref={chartLayoutRef}
                                  chartHeight={chartHeight}
                                  chartWidth={width}
                                  className={className}
                                  emptyState={emptyState}
                                  errorState={errorState}
                                  isEmpty={isEmptyData(children, data)}
                                  showLoader={loading}
                                  style={style}
                                  data-dtrum-mask={dataDtrumMask}
                                  data-dtrum-allow={dataDtrumAllow}
                                >
                                  <ChartLayout.Graph>
                                    <XYChartInternal
                                      config={config}
                                      ref={rootRef}
                                    />
                                  </ChartLayout.Graph>
                                  {!legendHidden && (
                                    <ChartLayout.Legend
                                      position={legendPosition}
                                      ratio={legendRatio}
                                      onResize={onRatioChange}
                                    >
                                      <XYChartLegend />
                                    </ChartLayout.Legend>
                                  )}
                                </ChartLayout>
                              </XYChartAnnotationsShownIndicatorsProvider>
                            </XYChartStoreProvider>
                          </XYChartColorScalesProvider>
                        </XYChartVisibleAxesProvider>
                      </XYChartAnnotationsDataProvider>
                    </XYChartChartDataProvider>
                  </XYChartAnnotationsConfig.Provider>
                </XYChartTruncationModeContext.Provider>
              </XYChartToolbarContext.Provider>
            </XYChartColorPaletteContext.Provider>
          </XYChartAxesConfigContext.Provider>
        </XYChartThresholdsContext.Provider>
      </XYChartLoadingContext.Provider>
    </XYChartIdProvider>
  );
});

/**
 * The `XYChart` encompasses various types of charts that utilize both x-axis and
 * y-axis for data representation, designed to visually display and analyze data
 * that involve two numerical variables.
 * @public
 */
export const XYChart = Object.assign(XYChartRoot, {
  Annotations,
  RectSeries,
  DotSeries,
  XAxis,
  YAxis,
  Legend,
  Toolbar,
  Intent,
  Threshold,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});

(XYChart as typeof XYChart & { displayName: string }).displayName = 'XYChart';

/**
 * XYAnnotations Slots
 * @public
 */
export const XYChartAnnotations = {
  Marker,
  Track,
  Tooltip: AnnotationsTooltip,
};
